import { Check, PenNib, ArrowCounterClockwise, Warning, GitBranch } from "@phosphor-icons/react";
import { Button } from "../ui/button";

const LOWER_BETTER = new Set(["Total owning cost $", "Material cost $", "Total losses kW", "No-load loss W", "Load loss W", "Total weight kg", "Hot spot °C"]);

function Delta({ row }) {
  const up = row.delta_pct > 0;
  const better = LOWER_BETTER.has(row.metric) ? !up : null;
  const color = better === null ? "var(--c-muted)" : row.delta_pct === 0 ? "var(--c-muted)" : better ? "var(--c-green)" : "var(--c-red)";
  return (
    <span className="font-mono" style={{ color }}>
      {row.delta_pct === 0 ? "—" : `${up ? "↑" : "↓"}${Math.abs(row.delta_pct)}%`}
    </span>
  );
}

function ImpactTable({ rows, testid, scenarioHeader }) {
  return (
    <div className="border border-[var(--c-border)] rounded-sm overflow-hidden mt-2" data-testid={testid}>
      <table className="w-full text-[10px]">
        <thead>
          <tr className="border-b border-[var(--c-border)] bg-[var(--c-bg)]">
            {[ "Metric", "Current", scenarioHeader || "After", "Δ" ].map((h) => (
              <th key={h} className="text-left px-2 py-1.5 font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.metric} className="border-b border-[var(--c-border)]/50 last:border-0">
              <td className="px-2 py-1.5 text-[var(--c-muted)]">{r.metric}</td>
              <td className="px-2 py-1.5 font-mono text-[var(--c-text)]">{r.current?.toLocaleString?.() ?? r.current}</td>
              <td className="px-2 py-1.5 font-mono text-[var(--c-accent)]">{r.scenario?.toLocaleString?.() ?? r.scenario}</td>
              <td className="px-2 py-1.5"><Delta row={r} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Warnings({ warnings }) {
  if (!warnings?.length) return null;
  return (
    <div className="mt-2 space-y-1">
      {warnings.map((w, i) => (
        <div key={i} className="flex items-start gap-1.5 text-[10px] text-[var(--c-red)]">
          <Warning size={11} className="shrink-0 mt-0.5" /> {w}
        </div>
      ))}
    </div>
  );
}

export default function ActionCard({ action, msgKey, appliedVersion, drawingSaved, busy, onApply, onUndo, onSaveDrawing }) {
  if (!action) return null;
  const kind = action.action;

  if (kind === "design_change") {
    return (
      <div className="mt-2 bg-[var(--c-bg)] border border-[var(--c-accent)]/40 rounded-sm p-3" data-testid={`action-card-${msgKey}`}>
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-accent)] mb-2 flex items-center gap-1.5">
          <GitBranch size={11} /> Design change
        </div>
        {action.changes?.map((c) => (
          <div key={c.param} className="flex justify-between text-xs py-1 border-b border-[var(--c-border)]/50 last:border-0">
            <span className="text-[var(--c-muted)] font-mono">{c.param}</span>
            <span className="font-mono text-[var(--c-text)]">{c.old} → <span className="text-[var(--c-accent)]">{c.value}</span></span>
          </div>
        ))}
        {action.impact && <ImpactTable rows={action.impact} testid={`impact-${msgKey}`} />}
        <Warnings warnings={action.warnings} />
        <div className="flex gap-2 mt-3">
          {!appliedVersion ? (
            <Button size="sm" data-testid={`apply-action-${msgKey}`} disabled={busy} onClick={() => onApply(action.changes)}
              className="bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm h-7 text-[10px]">
              <Check size={11} weight="bold" className="mr-1" /> Apply
            </Button>
          ) : (
            <>
              <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[var(--c-green)]" data-testid={`applied-note-${msgKey}`}>
                <Check size={11} weight="bold" /> Applied as v{appliedVersion}
              </span>
              <Button size="sm" variant="outline" data-testid={`undo-action-${msgKey}`} disabled={busy} onClick={onUndo}
                className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-7 text-[10px]">
                <ArrowCounterClockwise size={11} className="mr-1" /> Undo
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (kind === "drawing_change") {
    return (
      <div className="mt-2 bg-[var(--c-bg)] border border-[var(--c-accent)]/40 rounded-sm p-3" data-testid={`action-card-${msgKey}`}>
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-accent)] mb-2 flex items-center gap-1.5">
          <PenNib size={11} /> Drawing change · {action.drawing_type}
        </div>
        <div className="bg-[var(--c-accent)]/10 border border-[var(--c-accent)]/40 rounded-sm px-2.5 py-2 text-[10px] text-[var(--c-accent)] mb-2"
          data-testid={`drawing-queued-banner-${msgKey}`}>
          Drawing change queued — will be applied when Drawing Studio is activated
        </div>
        {action.changes?.map((c, i) => (
          <div key={i} className="text-xs text-[var(--c-text)] py-1 border-b border-[var(--c-border)]/50 last:border-0">
            {c.element}: <span className="font-mono text-[var(--c-muted)]">{c.operation} {JSON.stringify(c.params)}</span>
          </div>
        ))}
        <Button size="sm" data-testid={`save-drawing-${msgKey}`} disabled={busy || drawingSaved} onClick={() => onSaveDrawing(action)}
          className="mt-3 bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-7 text-[10px]">
          {drawingSaved ? "Saved to Project" : "Save to Project"}
        </Button>
      </div>
    );
  }

  if (kind === "what_if") {
    return (
      <div className="mt-2 bg-[var(--c-bg)] border border-[var(--c-amber)]/40 rounded-sm p-3" data-testid={`action-card-${msgKey}`}>
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-amber)] mb-1">
          What-if · {action.note || "scenario"}
        </div>
        <div className="text-[10px] text-[var(--c-muted)]">Comparison only — nothing was applied.</div>
        {action.comparison && <ImpactTable rows={action.comparison} testid={`whatif-${msgKey}`} scenarioHeader="Scenario" />}
        <Warnings warnings={action.warnings} />
      </div>
    );
  }

  if (kind === "fix_suggestion") {
    return (
      <div className="mt-2 bg-[var(--c-bg)] border border-[var(--c-green)]/40 rounded-sm p-3" data-testid={`action-card-${msgKey}`}>
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-green)] mb-2">Fix suggestion</div>
        {action.options?.map((o, i) => (
          <div key={i} className="border border-[var(--c-border)] rounded-sm p-2.5 mb-2 last:mb-0" data-testid={`fix-option-${msgKey}-${i}`}>
            <div className="text-xs text-[var(--c-text)]">{o.label}</div>
            <div className="text-[10px] text-[var(--c-muted)] mt-0.5">{o.rationale}</div>
            {o.result_metrics && (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[9px] text-[var(--c-muted)] mt-1.5">
                <span>Z <span className="text-[var(--c-text)]">{o.result_metrics.impedance_pct}%</span></span>
                <span>hot spot <span className="text-[var(--c-text)]">{o.result_metrics.hot_spot_c}°C</span></span>
                <span>losses <span className="text-[var(--c-text)]">{o.result_metrics.total_losses_kw} kW</span></span>
                <span>TOO <span className="text-[var(--c-text)]">${o.result_metrics.too_usd?.toLocaleString()}</span></span>
              </div>
            )}
            <Warnings warnings={o.warnings} />
            {!appliedVersion && (
              <Button size="sm" data-testid={`apply-fix-option-${msgKey}-${i}`} disabled={busy}
                onClick={() => onApply(Object.entries(o.inputs_patch).map(([param, value]) => ({ param, value })), `Copilot fix: ${o.label}`)}
                className="mt-2 bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm h-7 text-[10px]">
                <Check size={11} weight="bold" className="mr-1" /> Apply
              </Button>
            )}
          </div>
        ))}
        {appliedVersion && (
          <div className="flex items-center gap-2 mt-1">
            <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[var(--c-green)]">
              <Check size={11} weight="bold" /> Applied as v{appliedVersion}
            </span>
            <Button size="sm" variant="outline" data-testid={`undo-action-${msgKey}`} disabled={busy} onClick={onUndo}
              className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-7 text-[10px]">
              <ArrowCounterClockwise size={11} className="mr-1" /> Undo
            </Button>
          </div>
        )}
      </div>
    );
  }

  return null;
}
